// Event menus and booking rules (20260930100010), the widget's side. Pure: no
// DOM, no React, so the rules can be checked under Node.
//
//  - A minimum number of tickets per booking: the tickets for ONE event in ONE
//    cart, across every ticket type, must reach it before the cart is paid.
//    The server enforces it at payment (reason `event_minimum_not_met`); the
//    widget keeps "Add" off until the guest's choice, plus what is already in
//    their cart for the event, reaches it.
//  - One booking per sitting (a private sitting): once ANOTHER cart holds or
//    has booked the event, the payload says `is_booked` and the sitting can't
//    be picked. The guest's own cart may add more lines (the server allows it),
//    so a sitting this cart already holds is never "Booked" to this guest.
//  - Questions one per step, as "how many of each": every option group of a
//    chosen ticket type is its own step after the quantities, where the guest
//    sets a count per option ("Sparkling wine ×6, Gin & tonic ×4") instead of
//    answering once per guest. The counts are shared out over the guests
//    (distributeCounts) into the SAME per-attendee option ids the hold has
//    always received (p_selections), so the server sees no difference.
//  - One menu per booking (20260930170010): on a `one_per_booking` sitting the
//    normal menus are ONE choice for the whole group. Choosing another menu
//    moves the count to it, so only one normal menu ever has a quantity;
//    always-offered menus (vegetarian, kids) and plain ticket types keep their
//    own. The hold is unchanged: one per ticket type with a quantity. The
//    server refuses a second normal menu in one cart
//    (`event_one_menu_per_booking`).
//  - Sales windows and the "from" price: a sitting whose sales have closed
//    says so on its card and detail before the guest tries (the server's
//    `event_sales_closed`), and a card's "Book from …" is the cheapest menu a
//    group books, never the kids or vegetarian menu that goes alongside it.
//  - Seats (20261003220010): a ticket type either uses a seat (the default)
//    or is an extra on top of the sitting's seats (a children's menu). The
//    minimum per booking counts seats only ("Minimum 8 guests" is 8 adults,
//    however many children come too); "X left" and "Sold out" mean seats; the
//    seat steppers share the seats left, and an extra's stepper stops at its
//    own cap. The hold is unchanged.
//
// Every field is optional on the payload: absent means no minimum, open
// seating, guests mix the menus, and the widget behaves as it did before the
// migrations.
import type {
  WidgetEvent,
  WidgetEventTicketOptionGroup,
  WidgetEventTicketType,
} from "../types";

/** The event's minimum tickets per booking, or null for none. Only a whole
 *  number of 2 or more is a minimum (the column's check is 2–500); anything
 *  else, including a payload without the field, is no minimum. */
export function eventMinimum(
  event: Pick<WidgetEvent, "min_tickets_per_booking">,
): number | null {
  const m = event.min_tickets_per_booking;
  return typeof m === "number" && Number.isInteger(m) && m >= 2 ? m : null;
}

/** Tickets for this event already in the guest's cart, across every ticket
 *  type (each event line carries metadata.event_id). Any ticket makes the
 *  sitting the guest's own (bookedByAnother); the minimum counts seats
 *  (seatsInCart). */
export function ticketsInCart(
  items: ReadonlyArray<{ quantity: number; metadata?: Record<string, unknown> | null }>,
  eventId: string,
): number {
  let n = 0;
  for (const item of items) {
    if (item.metadata?.event_id !== eventId) continue;
    if (Number.isFinite(item.quantity) && item.quantity > 0) n += item.quantity;
  }
  return n;
}

/** How many more tickets the booking needs to reach the minimum: 0 when there
 *  is no minimum or the choice plus the cart already reach it. */
export function minimumShortfall(
  minimum: number | null,
  chosen: number,
  inCart: number,
): number {
  if (minimum === null) return 0;
  return Math.max(0, minimum - Math.max(0, chosen) - Math.max(0, inCart));
}

/** A private sitting another group has taken. A sitting this guest's cart
 *  already holds is theirs to add to, never "Booked". */
export function bookedByAnother(
  event: Pick<WidgetEvent, "is_booked" | "one_booking_only">,
  inCart: number,
): boolean {
  return event.is_booked === true && inCart === 0;
}

/** One question step: an option group of a chosen ticket type, answered as a
 *  count per option across that type's guests. */
export interface QuestionStep {
  key: string;
  type: WidgetEventTicketType;
  group: WidgetEventTicketOptionGroup;
  /** Guests on this ticket type (its chosen quantity). */
  quantity: number;
}

/** The question steps for a choice of quantities, in ticket-type order and,
 *  within a type, option-group order. A group without options asks nothing. */
export function questionSteps(
  types: ReadonlyArray<WidgetEventTicketType>,
  quantities: Readonly<Record<string, number>>,
): QuestionStep[] {
  const steps: QuestionStep[] = [];
  for (const type of types) {
    const quantity = quantities[type.id] ?? 0;
    if (!(quantity > 0)) continue;
    for (const group of type.option_groups ?? []) {
      if (group.options.length === 0) continue;
      steps.push({ key: `${type.id}:${group.id}`, type, group, quantity });
    }
  }
  return steps;
}

// ── How many of each ─────────────────────────────────────────────────────────

/** How many guests chose each option of one group: optionId → count. */
export type OptionCounts = Readonly<Record<string, number>>;

const whole = (n: unknown): number =>
  typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;

/** Whether the group is one choice per guest (max_select 1). */
export function isSingleChoice(group: WidgetEventTicketOptionGroup): boolean {
  return group.max_select <= 1;
}

/** The limits a group's counts must keep for `guests` guests.
 *  - One choice per guest: the counts add up to the guests when required
 *    (min_select ≥ 1), to no more than the guests when optional (a guest left
 *    over gets no pick).
 *  - Several per guest: each option at most once per guest (so at most
 *    `guests`), and in all between guests × min_select and guests × max_select. */
export function countLimits(
  group: WidgetEventTicketOptionGroup,
  guests: number,
): { minTotal: number; maxTotal: number; perOption: number } {
  const n = whole(guests);
  const optionCount = group.options.length;
  if (isSingleChoice(group)) {
    return { minTotal: group.min_select >= 1 ? n : 0, maxTotal: n, perOption: n };
  }
  const min = Math.max(0, Math.floor(group.min_select));
  const max = Math.max(min, Math.floor(group.max_select));
  // No guest can take more options than there are.
  const maxTotal = n * Math.min(max, optionCount);
  return { minTotal: Math.min(n * min, maxTotal), maxTotal, perOption: n };
}

/** The group's counts added up (options of the group only). */
export function countsTotal(group: WidgetEventTicketOptionGroup, counts: OptionCounts): number {
  let t = 0;
  for (const o of group.options) t += whole(counts[o.id]);
  return t;
}

/** Whether one more of an option still keeps the limits. */
export function canAddOne(
  group: WidgetEventTicketOptionGroup,
  counts: OptionCounts,
  optionId: string,
  guests: number,
): boolean {
  const { maxTotal, perOption } = countLimits(group, guests);
  return whole(counts[optionId]) < perOption && countsTotal(group, counts) < maxTotal;
}

/** Counts trimmed to fit `guests` (after the guest count on step 1 went
 *  down): each option to at most `guests`, then the total to its maximum,
 *  taking from the last options first. Counts that already fit come back as
 *  they were. */
export function clampCounts(
  group: WidgetEventTicketOptionGroup,
  counts: OptionCounts,
  guests: number,
): Record<string, number> {
  const { maxTotal, perOption } = countLimits(group, guests);
  const out: Record<string, number> = {};
  for (const o of group.options) out[o.id] = Math.min(whole(counts[o.id]), perOption);
  let over = countsTotal(group, out) - maxTotal;
  for (let i = group.options.length - 1; i >= 0 && over > 0; i--) {
    const id = group.options[i]!.id;
    const take = Math.min(out[id]!, over);
    out[id] = out[id]! - take;
    over -= take;
  }
  return out;
}

/** Why the counts can't go on yet, in plain words; null when they can. */
export function countsProblem(
  group: WidgetEventTicketOptionGroup,
  counts: OptionCounts,
  guests: number,
): string | null {
  const n = whole(guests);
  const { minTotal, maxTotal, perOption } = countLimits(group, n);
  const total = countsTotal(group, counts);
  const guestWord = (k: number) => (k === 1 ? "guest" : "guests");
  if (group.options.some((o) => whole(counts[o.id]) > perOption)) {
    return `Each guest can take an option only once: at most ${perOption} of any one.`;
  }
  if (total > maxTotal) {
    return isSingleChoice(group)
      ? `One each: at most ${n} in all.`
      : `At most ${maxTotal} in all (${group.max_select} per guest).`;
  }
  if (total < minTotal) {
    const short = minTotal - total;
    return isSingleChoice(group)
      ? `Choose for ${short} more ${guestWord(short)} to continue.`
      : `Choose ${short} more to continue: each guest needs at least ${group.min_select}.`;
  }
  return distributeCounts(group, counts, n) === null
    ? "These choices can't be shared out between the guests. Please change the counts."
    : null;
}

/** Share the counts out over `guests` guests, deterministically: every pick
 *  in option order goes to the next guest round the table (pick k to guest
 *  k mod guests). An option chosen at most `guests` times therefore never
 *  lands on one guest twice, and each guest gets ⌊total ÷ guests⌋ or one more
 *  picks — within min_select … max_select whenever the total is. Each guest's
 *  picks come back in option order. Null when the counts break a limit or the
 *  result would give any guest too few, too many or a repeated pick. */
export function distributeCounts(
  group: WidgetEventTicketOptionGroup,
  counts: OptionCounts,
  guests: number,
): string[][] | null {
  const n = whole(guests);
  if (n === 0) return [];
  const { minTotal, maxTotal, perOption } = countLimits(group, n);
  const total = countsTotal(group, counts);
  if (total < minTotal || total > maxTotal) return null;
  if (group.options.some((o) => whole(counts[o.id]) > perOption)) return null;
  const perGuest = shareOut(group, counts, n);
  // Belt and braces: the shape the server checks (min/max per guest, no repeats).
  const min = isSingleChoice(group) ? (group.min_select >= 1 ? 1 : 0) : group.min_select;
  const max = isSingleChoice(group) ? 1 : group.max_select;
  for (const picks of perGuest) {
    if (picks.length < min || picks.length > max) return null;
    if (new Set(picks).size !== picks.length) return null;
  }
  return perGuest;
}

/** The round-robin share-out itself, unchecked: pick k (in option order) to
 *  guest k mod guests. distributeCounts checks it; the running total uses it
 *  as it stands, so the price shown while choosing is the one the guests'
 *  picks so far would make. */
export function shareOut(
  group: WidgetEventTicketOptionGroup,
  counts: OptionCounts,
  guests: number,
): string[][] {
  const n = whole(guests);
  const perGuest: string[][] = Array.from({ length: n }, () => []);
  if (n === 0) return perGuest;
  let k = 0;
  for (const o of group.options) {
    const c = whole(counts[o.id]);
    for (let j = 0; j < c; j++, k++) perGuest[k % n]!.push(o.id);
  }
  return perGuest;
}

/** One ticket type's per-attendee option ids for the hold (p_selections):
 *  for each guest, their picks group by group in the type's group order —
 *  exactly the shape the one-select-per-guest form used to send. Groups
 *  without options add nothing. Null when any group's counts can't be shared
 *  out. */
export function lineSelectionsFromCounts(
  type: WidgetEventTicketType,
  countsByGroup: Readonly<Record<string, OptionCounts>>,
  quantity: number,
): string[][] | null {
  const n = whole(quantity);
  const lines: string[][] = Array.from({ length: n }, () => []);
  for (const group of type.option_groups ?? []) {
    if (group.options.length === 0) continue;
    const shared = distributeCounts(group, countsByGroup[group.id] ?? {}, n);
    if (shared === null) return null;
    shared.forEach((picks, i) => lines[i]!.push(...picks));
  }
  return lines;
}

/** What a question asks, in plain words, for `guests` guests. */
export function questionRule(group: WidgetEventTicketOptionGroup, guests: number): string {
  const n = whole(guests);
  const { minTotal, maxTotal } = countLimits(group, n);
  const required = group.min_select >= 1;
  if (isSingleChoice(group)) {
    return required
      ? `Required · one each: set how many of each so they add up to ${n}.`
      : `Optional · one each, up to ${n} in all. Guests you leave out get none.`;
  }
  const perGuest =
    group.min_select === group.max_select
      ? `${group.max_select}`
      : required
        ? `${group.min_select} to ${group.max_select}`
        : `up to ${group.max_select}`;
  const range = required && minTotal !== maxTotal ? `${minTotal} to ${maxTotal}` : required ? `${minTotal}` : `up to ${maxTotal}`;
  return `${required ? "Required" : "Optional"} · each guest picks ${perGuest}: ${range} in all, at most ${n} of any one.`;
}

/** The running line above the options: "4 of 6 chosen". */
export function countsProgress(
  group: WidgetEventTicketOptionGroup,
  counts: OptionCounts,
  guests: number,
): string {
  const total = countsTotal(group, counts);
  const { minTotal, maxTotal } = countLimits(group, guests);
  if (isSingleChoice(group)) return `${total} of ${maxTotal} chosen`;
  return minTotal > 0 && minTotal !== maxTotal
    ? `${total} chosen · ${minTotal} to ${maxTotal} needed`
    : `${total} of ${maxTotal} chosen`;
}

/** The guest's words for a refused minimum, with the numbers when the server
 *  sent them (`minimum`, `quantity` on event_minimum_not_met). */
export function minimumNotMetMessage(minimum?: number | null, quantity?: number | null): string {
  if (typeof minimum === "number" && minimum > 0) {
    const have = typeof quantity === "number" && quantity >= 0 ? quantity : null;
    const more = have !== null ? Math.max(1, minimum - have) : null;
    return more !== null
      ? `This event takes bookings of ${minimum} guests or more, and yours has ${have}. Add ${more} more ${more === 1 ? "guest" : "guests"} to continue.`
      : `This event takes bookings of ${minimum} guests or more. Add more guests to continue.`;
  }
  return "This event has a minimum number of guests per booking. Add more guests to continue.";
}

/** The guest's words when another group has just taken a private sitting. */
export const ALREADY_BOOKED_MESSAGE =
  "Sorry, another group has just booked this private sitting. Please choose another sitting.";

// ── One menu per booking (20260930170010) ────────────────────────────────────

export type MenuChoice = "mix" | "one_per_booking";

/** The sitting's menu choice. Anything but `one_per_booking`, including a
 *  payload from before the migration, is `mix`: today's behaviour. */
export function menuChoiceOf(event: Pick<WidgetEvent, "menu_choice">): MenuChoice {
  return event.menu_choice === "one_per_booking" ? "one_per_booking" : "mix";
}

/** An always-offered menu (vegetarian, kids): booked alongside whatever menu
 *  the group has. Absent = false. */
export function isAlwaysOffered(type: Pick<WidgetEventTicketType, "always_offered">): boolean {
  return type.always_offered === true;
}

/** A normal menu: the ticket type serves a library menu that is not always
 *  offered. A ticket type with no menu link is a plain ticket type. */
export function isNormalMenu(
  type: Pick<WidgetEventTicketType, "menu_id" | "always_offered">,
): boolean {
  return typeof type.menu_id === "string" && type.menu_id !== "" && !isAlwaysOffered(type);
}

/** The most of one ticket type the stepper allows: its own cap, and what is
 *  left of it now when the payload says (`remaining`), else 99. */
export function ticketTypeMax(
  type: Pick<WidgetEventTicketType, "quantity_available" | "remaining">,
): number {
  let max = 99;
  for (const cap of [type.quantity_available, type.remaining]) {
    if (typeof cap === "number" && Number.isFinite(cap)) max = Math.min(max, Math.max(0, Math.floor(cap)));
  }
  return max;
}

/** The normal menus the guest chooses ONE of, in ticket-type order: a
 *  `one_per_booking` sitting offering two or more. Otherwise [] and every
 *  ticket type keeps its own stepper — on a mix sitting as today, and with a
 *  single normal menu its stepper already is the one menu. A payload without
 *  `menu_id` has no normal menus, so it also stays as today (the server still
 *  refuses a second menu, in the guest's words). */
export function singleChoiceMenus<
  T extends Pick<WidgetEventTicketType, "menu_id" | "always_offered">,
>(event: Pick<WidgetEvent, "menu_choice">, types: ReadonlyArray<T>): T[] {
  if (menuChoiceOf(event) !== "one_per_booking") return [];
  const menus = types.filter(isNormalMenu);
  return menus.length >= 2 ? menus : [];
}

/** The normal menu this guest's cart already holds for the event (its ticket
 *  type id), or null. The server allows more of that menu, never another. */
export function menuInCart(
  items: ReadonlyArray<{ quantity: number; metadata?: Record<string, unknown> | null }>,
  eventId: string,
  menus: ReadonlyArray<Pick<WidgetEventTicketType, "id">>,
): string | null {
  const ids = new Set(menus.map((m) => m.id));
  for (const item of items) {
    if (item.metadata?.event_id !== eventId) continue;
    const typeId = item.metadata?.ticket_type_id;
    if (typeof typeId === "string" && ids.has(typeId) && item.quantity > 0) return typeId;
  }
  return null;
}

/** The menu shown as chosen: the normal menu with a count; else the one the
 *  guest picked (still offered) before setting a count; else the one their
 *  cart already holds for the event; else none yet. */
export function chosenMenuId(
  menus: ReadonlyArray<Pick<WidgetEventTicketType, "id">>,
  quantities: Readonly<Record<string, number>>,
  picked: string | null,
  inCart: string | null,
): string | null {
  const counted = menus.find((m) => whole(quantities[m.id]) > 0);
  if (counted) return counted.id;
  if (picked !== null && menus.some((m) => m.id === picked)) return picked;
  if (inCart !== null && menus.some((m) => m.id === inCart)) return inCart;
  return null;
}

/** Choose `toId` as the booking's one menu. The count on the normal menus
 *  (one menu's; all of it, should more than one ever have any) moves to the
 *  new menu, up to that menu's own cap, and every other normal menu goes to
 *  0. Always-offered menus and plain ticket types keep their counts, so the
 *  total, and the minimum per booking counted from it, carry over. Choosing
 *  the menu already chosen, or one that isn't offered, changes nothing. */
export function pickOneMenu(
  menus: ReadonlyArray<Pick<WidgetEventTicketType, "id" | "quantity_available">>,
  quantities: Readonly<Record<string, number>>,
  toId: string,
): Record<string, number> {
  const next: Record<string, number> = { ...quantities };
  const to = menus.find((m) => m.id === toId);
  if (!to) return next;
  let moving = 0;
  for (const m of menus) moving += whole(quantities[m.id]);
  for (const m of menus) if (m.id !== toId && m.id in next) next[m.id] = 0;
  next[toId] = Math.min(moving, ticketTypeMax(to));
  return next;
}

/** The guest's words for `event_one_menu_per_booking`: the cart already has
 *  another normal menu for a one-menu-per-booking sitting. */
export const ONE_MENU_PER_BOOKING_MESSAGE =
  "This sitting takes one menu for the whole group. Please choose one menu for everyone. The vegetarian and kids menus can still be added.";

// ── Sales windows and the card's "from" price ────────────────────────────────

/** A sales window has closed once its close time is set and reached: the
 *  server refuses a hold from that instant (now() >= sales_close_at). Absent,
 *  null or unparseable = open. */
function windowClosed(closeAt: string | null | undefined, now: number): boolean {
  if (!closeAt) return false;
  const at = Date.parse(closeAt);
  return Number.isFinite(at) && now >= at;
}

/** A ticket type whose own sales window has closed (the server's
 *  `ticket_type_sales_closed`). */
export function ticketTypeSalesClosed(
  type: Pick<WidgetEventTicketType, "sales_close_at">,
  now: number,
): boolean {
  return windowClosed(type.sales_close_at, now);
}

/** Bookings for the sitting have closed: its sales_close_at has passed (the
 *  server's `event_sales_closed`), or it has ticket types and every one's own
 *  window has closed, so nothing is left to book. The cards read "Bookings
 *  closed" and the detail can't add. */
export function salesClosed(
  event: Pick<WidgetEvent, "sales_close_at" | "ticket_types">,
  now: number,
): boolean {
  if (windowClosed(event.sales_close_at, now)) return true;
  const types = event.ticket_types ?? [];
  return types.length > 0 && types.every((t) => ticketTypeSalesClosed(t, now));
}

/** The price a card's "Book from …" leads with: the cheapest priced ticket
 *  type a group books as its menu. An always-offered menu (vegetarian, kids)
 *  goes alongside a menu, never instead of one, so it is left out, as is an
 *  extra that doesn't use a seat, and a ticket type whose own sales have
 *  closed or whose cap is 0. With none left
 *  (no ticket types in the payload, or every one always offered) it is the
 *  payload's cheapest_price_cents, the cheapest enabled ticket type of all. */
export function eventFromPriceCents(
  event: Pick<WidgetEvent, "cheapest_price_cents" | "ticket_types">,
  now: number,
): number | null {
  let from: number | null = null;
  for (const t of event.ticket_types ?? []) {
    if (isAlwaysOffered(t) || !usesSeat(t) || ticketTypeSalesClosed(t, now)) continue;
    if (t.quantity_available === 0) continue;
    if (!(typeof t.price_cents === "number" && t.price_cents > 0)) continue;
    if (from === null || t.price_cents < from) from = t.price_cents;
  }
  return from ?? event.cheapest_price_cents ?? null;
}

// ── Seats (20261003220010) ───────────────────────────────────────────────────

/** A ticket type takes a seat unless the payload says it doesn't. */
export function usesSeat(type: Pick<WidgetEventTicketType, "uses_seat">): boolean {
  return type.uses_seat !== false;
}

/** The guest's words under a ticket type that doesn't use a seat. */
export const EXTRA_TICKET_NOTE = "Extra: doesn't count toward the group size";

/** The seats the chosen quantities take: seat-using ticket types only. */
export function seatsChosen(
  types: ReadonlyArray<Pick<WidgetEventTicketType, "id" | "uses_seat">>,
  quantities: Readonly<Record<string, number>>,
): number {
  let n = 0;
  for (const t of types) if (usesSeat(t)) n += whole(quantities[t.id]);
  return n;
}

/** Seat tickets for this event already in the guest's cart. A line's ticket
 *  type is looked up among the event's types; one the payload doesn't list
 *  counts as a seat, as the server counts it. */
export function seatsInCart(
  items: ReadonlyArray<{ quantity: number; metadata?: Record<string, unknown> | null }>,
  eventId: string,
  types: ReadonlyArray<Pick<WidgetEventTicketType, "id" | "uses_seat">>,
): number {
  const extras = new Set(types.filter((t) => !usesSeat(t)).map((t) => t.id));
  let n = 0;
  for (const item of items) {
    if (item.metadata?.event_id !== eventId) continue;
    const typeId = item.metadata?.ticket_type_id;
    if (typeof typeId === "string" && extras.has(typeId)) continue;
    if (Number.isFinite(item.quantity) && item.quantity > 0) n += item.quantity;
  }
  return n;
}

const finite = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

/** Seats left on the sitting, or null when the payload doesn't say:
 *  seats_remaining (net of live seat holds) when present; else, for a
 *  payload that carries total_capacity and sold_count, their difference. */
export function eventSeatsRemaining(
  event: Pick<WidgetEvent, "seats_remaining" | "total_capacity" | "sold_count">,
): number | null {
  if (finite(event.seats_remaining)) return Math.max(0, Math.floor(event.seats_remaining));
  if (finite(event.total_capacity) && finite(event.sold_count)) {
    return Math.max(0, event.total_capacity - event.sold_count);
  }
  return null;
}

/** A card's seat state: "Sold out" at no seat left, "X left" from 1 to the
 *  larger of 5 and a tenth of the seats. Unknown seats = neither. */
export function eventSeatState(
  event: Pick<WidgetEvent, "seats_remaining" | "total_capacity" | "sold_count">,
): { remaining: number | null; soldOut: boolean; lowStock: boolean } {
  const remaining = eventSeatsRemaining(event);
  const capacity = finite(event.total_capacity) ? event.total_capacity : null;
  return {
    remaining,
    soldOut: remaining === 0,
    lowStock:
      remaining !== null && capacity !== null && remaining > 0 &&
      remaining <= Math.max(5, Math.floor(capacity * 0.1)),
  };
}

/** The most the stepper of one ticket type allows beside the others: an
 *  extra stops at its own cap; a seat type at its own cap and at the seats
 *  left after the other seat types' choices. seatsLeft null = no seat cap. */
export function stepperMax(
  type: Pick<WidgetEventTicketType, "id" | "uses_seat" | "quantity_available" | "remaining">,
  types: ReadonlyArray<Pick<WidgetEventTicketType, "id" | "uses_seat">>,
  quantities: Readonly<Record<string, number>>,
  seatsLeft: number | null,
): number {
  const own = ticketTypeMax(type);
  if (!usesSeat(type) || seatsLeft === null) return own;
  const others = seatsChosen(types, quantities) - whole(quantities[type.id]);
  return Math.max(0, Math.min(own, seatsLeft - others));
}
